/**
 * Keys whose values are ISO-8601 timestamps on the wire (Prisma `DateTime`
 * columns serialised by `JSON.stringify`). Only these are turned back into
 * `Date`s — never a value that merely *looks* like a date, so a message
 * whose text is "12/05/2024" stays a string.
 */
const DATE_KEYS = new Set([
  "createdAt",
  "updatedAt",
  "deletedAt",
  "uploadedAt",
  "addedAt",
  "expiresAt",
  "completedAt",
  "lastUserSeenAt",
  "lastBotSeenAt",
]);

/**
 * Walk an API payload and convert the values under known date keys from ISO
 * strings to `Date`. Returns the same value (mutated in place) so callers can
 * use it inline; primitives pass through untouched.
 */
export function reviveDates<T>(value: T): T {
  walk(value);
  return value;
}

function walk(value: unknown): void {
  if (Array.isArray(value)) {
    value.forEach(walk);
    return;
  }
  if (value === null || typeof value !== "object" || value instanceof Date) {
    return;
  }

  const record = value as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const child = record[key];
    if (DATE_KEYS.has(key) && typeof child === "string") {
      const date = new Date(child);
      if (!Number.isNaN(date.getTime())) {
        record[key] = date;
      }
      continue;
    }
    walk(child);
  }
}
